import { useEffect, useState } from "react";
import {
  Alert,
  Backdrop,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
} from "@mui/material";
import axios from "axios.js";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DataTable from "theme/Tables/DataTable";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";

function AgentGroupCard({
  campaignRef,
  agentGroups,
  campaignAgentGroups,
  aggregators,
  campaignAggregators,
  triggerRefresh
}) {
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [tableRows, setTableRows] = useState([]);
  const [selectedAgentGroup, setSelectedAgentGroup] = useState(null);
  const [openNewSuperAgentDialog, setOpenNewSuperAgentDialog] = useState(false);

  const [aggregatorTableRows, setAggregatorTableRows] = useState([]);
  const [selectedAggregator, setSelectedAggregator] = useState(null);
  const [aggregatorQuota, setAggregatorQuota] = useState(null);
  const [openAddAggregatorDialog, setOpenAddAggregatorDialog] = useState(false);

  async function deleteAgentGroup(ref) {
    setShowLoading(true);
    await axios
      .get(`/portal/campaigns/agentGroups/${ref}/remove.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function updateAgentGroupStatus(ref) {
    setShowLoading(true);
    await axios
      .get(`/portal/campaigns/agentGroups/${ref}/status.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function createSuperAgent() {
    if (selectedAgentGroup === null) {
      setSeverity("error");
      setErrMsg("An agent group is required.");
      setOpen(true);
      return;
    }

    setOpenNewSuperAgentDialog(false);
    setShowLoading(true);
    const json = {
      campaign: campaignRef,
      group: selectedAgentGroup,
      commission: 0,
    };

    await axios
      .post("portal/campaigns/agent_group/assign.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg("Agent group assigned!");
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const campaignAgentGroupTableColumns = [
    { Header: "Name", accessor: "name", align: "left" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Actions", accessor: "actions", align: "center" },
    { Header: "Added On", accessor: "created", align: "center" },
  ];

  const buildCampaignAgentGroupRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        name: row.title,
        status: (
          <MDBox>
            <Icon color={row.status ? "success" : "error"}>
              {row.status ? "check_circle" : "cancel"}
            </Icon>
          </MDBox>
        ),
        actions: (
          <MDBox mt={1} variant="button">
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              iconOnly
              onClick={() => {
                if (window.confirm(`${row.status ? "Disable" : "Enable"} this group?`) === true) {
                  updateAgentGroupStatus(row.reference);
                }
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                {row.status ? "pause" : "play_arrow"}
              </Icon>
            </MDButton>
            &nbsp; &nbsp;
            <MDButton
              size="small"
              variant="outlined"
              color="error"
              iconOnly
              onClick={() => {
                if (window.confirm(`Remove this group?`) === true) {
                  deleteAgentGroup(row.reference);
                }
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                delete
              </Icon>
            </MDButton>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  async function deleteAggregator(ref) {
    setShowLoading(true);
    await axios
      .get(`/portal/campaigns/aggregators/${ref}/remove.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function updateAggregatorStatus(ref) {
    setShowLoading(true);
    await axios
      .get(`/portal/campaigns/aggregators/${ref}/status.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function assignAggregator() {
    if (selectedAggregator === null) {
      setSeverity("error");
      setErrMsg("A supervisor is required.");
      setOpen(true);
      return;
    }

    setOpenAddAggregatorDialog(false);
    setShowLoading(true);
    const json = {
      campaign: campaignRef,
      aggregator: selectedAggregator,
      commission: 0,
      quota: aggregatorQuota,
    };

    await axios
      .post("portal/campaigns/aggregators/assign.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const aggregatorsTableColumns = [
    { Header: "Name", accessor: "name", align: "left" },
    { Header: "Quota", accessor: "quota", align: "center" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Actions", accessor: "actions", align: "right" },
    // { Header: "Added On", accessor: "created", align: "center" },
  ];

  const buildAggregatorsRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        name: row.title,
        quota: row.quota ? row.quota : "Unlimited",
        status: (
          <MDBox>
            <Icon color={row.status ? "success" : "error"}>
              {row.status ? "check_circle" : "cancel"}
            </Icon>
          </MDBox>
        ),
        actions: (
          <MDBox mt={1} variant="button">
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              iconOnly
              onClick={() => {
                if (
                  window.confirm(`${row.status ? "Disable" : "Enable"} this supervisor?`) === true
                ) {
                  updateAggregatorStatus(row.reference);
                }
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                {row.status ? "pause" : "play_arrow"}
              </Icon>
            </MDButton>
            &nbsp;
            <MDButton
              size="small"
              variant="outlined"
              color="error"
              iconOnly
              onClick={() => {
                if (window.confirm(`Remove this supervisor?`) === true) {
                  deleteAggregator(row.reference);
                }
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                delete
              </Icon>
            </MDButton>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const rows1 = buildCampaignAgentGroupRows(campaignAgentGroups);
    setTableRows(rows1);

    const rows2 = buildAggregatorsRows(campaignAggregators);
    setAggregatorTableRows(rows2);
  }, [campaignAgentGroups, campaignAggregators]);

  return (
    <MDBox>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openNewSuperAgentDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Assign Agent Group</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mt={2}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Agent Group</InputLabel>
                <Select
                  label="Agent Group"
                  onChange={(e) => {
                    setSelectedAgentGroup(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    agentGroups.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      agentGroups.map((record) => (
                        <MenuItem value={`${record.reference}`}>
                          {record.agent_group.title}
                        </MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenNewSuperAgentDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              createSuperAgent();
            }}
          >
            Assign
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openAddAggregatorDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Assign Supervisor</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mt={2}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Supervisor</InputLabel>
                <Select
                  label="Supervisor"
                  onChange={(e) => {
                    setSelectedAggregator(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    aggregators.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      aggregators.map((row) => (
                        <MenuItem value={`${row.reference}`}>{row.title}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </MDBox>
            <MDBox mt={2}>
              <MDInput
                name="title"
                margin="dense"
                label="Agent Quota (Optional)"
                type="number"
                fullWidth
                variant="outlined"
                onChange={(e) => setAggregatorQuota(e.target.value)}
              />
              <MDBox mt={1}>
                <Alert severity="info">
                  <MDTypography fontSize={11}>
                    Set how many agents this supervisor can assign to your campaign. Leave blank for
                    an unlimited number.
                  </MDTypography>
                </Alert>
              </MDBox>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenAddAggregatorDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              assignAggregator();
            }}
          >
            Assign
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox mt={3}>
        <Card sx={{ height: "100%", marginTop: "0px" }}>
          <MDBox>
            <MDBox p={2}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="light">
                  <b>Agent Groups</b>
                  <br />
                  <small>Assign agent groups to campaign.</small>
                </MDTypography>
                <MDButton
                  variant="contained"
                  color="dark"
                  iconOnly
                  onClick={() => {
                    setSelectedAgentGroup(null);
                    setOpenNewSuperAgentDialog(true);
                  }}
                >
                  <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                </MDButton>
              </MDBox>
            </MDBox>
            <MDBox>
              <DataTable
                table={{ columns: campaignAgentGroupTableColumns, rows: tableRows }}
                isSorted
                entriesPerPage={false}
                showTotalEntries
                buttons
                noEndBorder
              />
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
      <MDBox mt={3}>
        <Card sx={{ height: "100%", marginTop: "0px" }}>
          <MDBox>
            <MDBox p={2}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="light">
                  <b>Supervisors</b>
                  <br />
                  <small>Assign supervisors to campaign.</small>
                </MDTypography>
                <MDButton
                  variant="contained"
                  color="dark"
                  iconOnly
                  onClick={() => {
                    setSelectedAggregator(null);
                    setOpenAddAggregatorDialog(true);
                  }}
                >
                  <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                </MDButton>
              </MDBox>
            </MDBox>
            <MDBox>
              <DataTable
                table={{ columns: aggregatorsTableColumns, rows: aggregatorTableRows }}
                isSorted
                entriesPerPage={false}
                showTotalEntries
                buttons
                noEndBorder
              />
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </MDBox>
  );
}

export default AgentGroupCard;
